<!-- FieldCommunicationsPanel.vue - Panel de comunicaciones -->
<template>
  <div class="field-communications-panel">
    <!-- Resumen de comunicaciones -->
    <v-row class="mb-4">
      <v-col cols="3">
        <v-card class="text-center pa-2" color="info" variant="outlined">
          <v-card-text>
            <v-icon size="24" class="mb-1">mdi-message-text</v-icon>
            <div class="text-h6">{{ unreadMessages }}</div>
            <div class="text-caption">No leídos</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="3">
        <v-card class="text-center pa-2" color="warning" variant="outlined">
          <v-card-text>
            <v-icon size="24" class="mb-1">mdi-alert</v-icon>
            <div class="text-h6">{{ activeAlerts }}</div>
            <div class="text-caption">Alertas</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="3">
        <v-card class="text-center pa-2" color="error" variant="outlined">
          <v-card-text>
            <v-icon size="24" class="mb-1">mdi-alert-octagon</v-icon>
            <div class="text-h6">{{ emergencies }}</div>
            <div class="text-caption">Emergencias</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="3">
        <v-card class="text-center pa-2" color="success" variant="outlined">
          <v-card-text>
            <v-icon size="24" class="mb-1">mdi-account-check</v-icon>
            <div class="text-h6">{{ activeContacts }}</div>
            <div class="text-caption">Activos</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filtros de comunicación -->
    <v-card class="mb-4" variant="outlined">
      <v-card-text class="pb-2">
        <v-row>
          <v-col cols="8">
            <v-btn-toggle
              v-model="activeTab"
              density="compact"
              mandatory
              class="w-100"
            >
              <v-btn value="messages" prepend-icon="mdi-message">
                Mensajes
              </v-btn>
              <v-btn value="alerts" prepend-icon="mdi-alert">
                Alertas
              </v-btn>
              <v-btn value="contacts" prepend-icon="mdi-contacts">
                Contactos
              </v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="4">
            <v-select
              v-model="priorityFilter"
              :items="priorityOptions"
              density="compact"
              placeholder="Prioridad"
              hide-details
              clearable
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Panel de mensajes -->
    <div v-if="activeTab === 'messages'" class="communications-content">
      <div class="d-flex justify-space-between align-center mb-3">
        <h3 class="text-h6">Mensajes Recientes</h3>
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          size="small"
          @click="composeMessage"
        >
          Nuevo
        </v-btn>
      </div>

      <div class="messages-list">
        <v-card
          v-for="message in filteredMessages"
          :key="message.id"
          class="mb-2"
          :class="getMessageClass(message)"
          variant="outlined"
          @click="selectMessage(message)"
        >
          <v-card-text class="py-2">
            <div class="d-flex align-center">
              <v-avatar :color="getContactColor(message.sender)" size="32" class="mr-3">
                <span class="text-white text-caption">
                  {{ getInitials(message.sender) }}
                </span>
              </v-avatar>
              
              <div class="flex-grow-1">
                <div class="d-flex justify-space-between align-center">
                  <div>
                    <div class="text-body-2 font-weight-medium">
                      {{ message.sender }}
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      {{ message.subject || 'Sin asunto' }}
                    </div>
                  </div>
                  
                  <div class="text-right">
                    <div class="text-caption">
                      {{ formatTime(message.timestamp) }}
                    </div>
                    <v-chip
                      v-if="message.priority !== 'normal'"
                      :color="getPriorityColor(message.priority)"
                      size="x-small"
                      class="mt-1"
                    >
                      {{ message.priority.toUpperCase() }}
                    </v-chip>
                  </div>
                </div>
                
                <div class="text-body-2 mt-2" style="color: rgba(0,0,0,0.7);">
                  {{ truncateText(message.content, 100) }}
                </div>
              </div>

              <div class="ml-2">
                <v-icon
                  v-if="!message.read"
                  color="primary"
                  size="12"
                >
                  mdi-circle
                </v-icon>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </div>

    <!-- Panel de alertas -->
    <div v-else-if="activeTab === 'alerts'" class="communications-content">
      <div class="d-flex justify-space-between align-center mb-3">
        <h3 class="text-h6">Alertas del Sistema</h3>
        <v-btn
          color="warning"
          prepend-icon="mdi-plus"
          size="small"
          @click="createAlert"
        >
          Nueva Alerta
        </v-btn>
      </div>

      <div class="alerts-list">
        <v-card
          v-for="alert in filteredAlerts"
          :key="alert.id"
          class="mb-3"
          :class="getAlertClass(alert)"
          variant="outlined"
        >
          <v-card-title class="d-flex align-center">
            <v-icon
              :color="getAlertColor(alert.type)"
              class="mr-3"
              size="24"
            >
              {{ getAlertIcon(alert.type) }}
            </v-icon>
            {{ alert.title }}
            <v-spacer></v-spacer>
            <v-chip
              :color="getPriorityColor(alert.priority)"
              size="small"
              variant="flat"
            >
              {{ alert.priority.toUpperCase() }}
            </v-chip>
          </v-card-title>
          
          <v-card-text>
            <div class="mb-2">{{ alert.description }}</div>
            
            <div class="d-flex justify-space-between text-caption text-medium-emphasis mb-2">
              <span>{{ formatDateTime(alert.created_at) }}</span>
              <span v-if="alert.location">📍 {{ alert.location }}</span>
            </div>

            <div v-if="alert.affected_teams?.length" class="mb-2">
              <div class="text-caption mb-1">Equipos afectados:</div>
              <div>
                <v-chip
                  v-for="team in alert.affected_teams"
                  :key="team"
                  size="small"
                  class="mr-1 mb-1"
                  color="info"
                  variant="outlined"
                >
                  {{ team }}
                </v-chip>
              </div>
            </div>

            <v-progress-linear
              v-if="alert.progress !== undefined"
              :model-value="alert.progress"
              :color="getAlertColor(alert.type)"
              height="6"
              rounded
              class="mt-2"
            ></v-progress-linear>
          </v-card-text>

          <v-card-actions>
            <v-btn
              v-if="alert.status === 'active'"
              @click="acknowledgeAlert(alert)"
              color="info"
              size="small"
              prepend-icon="mdi-check"
            >
              Reconocer
            </v-btn>
            
            <v-btn
              @click="resolveAlert(alert)"
              color="success"
              size="small"
              prepend-icon="mdi-check-all"
            >
              Resolver
            </v-btn>
            
            <v-btn
              @click="escalateAlert(alert)"
              color="error"
              size="small"
              prepend-icon="mdi-arrow-up-bold"
            >
              Escalar
            </v-btn>

            <v-spacer></v-spacer>
            
            <v-btn
              icon="mdi-share"
              size="small"
              variant="text"
              @click="shareAlert(alert)"
            ></v-btn>
          </v-card-actions>
        </v-card>
      </div>
    </div>

    <!-- Panel de contactos -->
    <div v-else-if="activeTab === 'contacts'" class="communications-content">
      <div class="d-flex justify-space-between align-center mb-3">
        <h3 class="text-h6">Contactos Activos</h3>
        <v-text-field
          v-model="contactSearch"
          density="compact"
          placeholder="Buscar contacto..."
          prepend-inner-icon="mdi-magnify"
          hide-details
          style="max-width: 200px;"
        ></v-text-field>
      </div>

      <div class="contacts-list">
        <v-card
          v-for="contact in filteredContacts"
          :key="contact.id"
          class="mb-2"
          variant="outlined"
        >
          <v-card-text class="py-2">
            <div class="d-flex align-center">
              <v-badge
                :color="getStatusColor(contact.status)"
                dot
                location="bottom end"
              >
                <v-avatar :color="getContactColor(contact.name)" size="40">
                  <span class="text-white">{{ getInitials(contact.name) }}</span>
                </v-avatar>
              </v-badge>
              
              <div class="ml-3 flex-grow-1">
                <div class="text-body-2 font-weight-medium">{{ contact.name }}</div>
                <div class="text-caption text-medium-emphasis">{{ contact.role }}</div>
                <div class="text-caption">{{ contact.location || 'Ubicación desconocida' }}</div>
              </div>
              
              <div class="contact-actions">
                <v-btn
                  icon="mdi-message"
                  size="small"
                  variant="text"
                  @click="sendMessage(contact)"
                ></v-btn>
                <v-btn
                  icon="mdi-phone"
                  size="small"
                  variant="text"
                  @click="callContact(contact)"
                ></v-btn>
                <v-btn
                  icon="mdi-map-marker"
                  size="small"
                  variant="text"
                  @click="locateContact(contact)"
                ></v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </div>

    <!-- Botón de emergencia -->
    <v-btn
      block
      color="error"
      prepend-icon="mdi-alarm-light"
      class="mt-4"
      @click="$emit('emergency-broadcast')"
    >
      Difusión de Emergencia
    </v-btn>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  messages: { type: Array, default: () => [] },
  alerts: { type: Array, default: () => [] },
  contacts: { type: Array, default: () => [] }
})

const emit = defineEmits([
  'send-message',
  'acknowledge-alert',
  'resolve-alert',
  'escalate-alert',
  'emergency-broadcast'
])

// Reactive data
const activeTab = ref('messages')
const priorityFilter = ref(null)
const contactSearch = ref('')

// Options
const priorityOptions = [
  { title: 'Baja', value: 'low' },
  { title: 'Normal', value: 'normal' },
  { title: 'Alta', value: 'high' },
  { title: 'Crítica', value: 'critical' }
]

// Computed properties
const unreadMessages = computed(() =>
  props.messages.filter(m => !m.read).length
)

const activeAlerts = computed(() =>
  props.alerts.filter(a => a.status === 'active').length
)

const emergencies = computed(() =>
  props.alerts.filter(a => a.priority === 'critical').length
)

const activeContacts = computed(() =>
  props.contacts.filter(c => c.status === 'online').length
)

const filteredMessages = computed(() => {
  let filtered = props.messages
  if (priorityFilter.value) {
    filtered = filtered.filter(m => m.priority === priorityFilter.value)
  }
  return filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
})

const filteredAlerts = computed(() => {
  let filtered = props.alerts
  if (priorityFilter.value) {
    filtered = filtered.filter(a => a.priority === priorityFilter.value)
  }
  return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
})

const filteredContacts = computed(() => {
  let filtered = props.contacts
  if (contactSearch.value) {
    filtered = filtered.filter(c =>
      c.name.toLowerCase().includes(contactSearch.value.toLowerCase()) ||
      c.role.toLowerCase().includes(contactSearch.value.toLowerCase())
    )
  }
  return filtered.sort((a, b) => {
    const statusOrder = { online: 0, busy: 1, away: 2, offline: 3 }
    return statusOrder[a.status] - statusOrder[b.status]
  })
})

// Methods
const getInitials = (name) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase()
}

const getContactColor = (name) => {
  const colors = ['primary', 'info', 'success', 'warning', 'error', 'purple']
  const hash = name.split('').reduce((a, b) => a + b.charCodeAt(0), 0)
  return colors[hash % colors.length]
}

const getStatusColor = (status) => {
  const colors = {
    online: 'success',
    busy: 'warning',
    away: 'info',
    offline: 'grey'
  }
  return colors[status] || 'grey'
}

const getPriorityColor = (priority) => {
  const colors = {
    low: 'success',
    normal: 'info',
    high: 'warning',
    critical: 'error'
  }
  return colors[priority] || 'grey'
}

const getAlertColor = (type) => {
  const colors = {
    system: 'info',
    security: 'error',
    maintenance: 'warning',
    operational: 'primary'
  }
  return colors[type] || 'grey'
}

const getAlertIcon = (type) => {
  const icons = {
    system: 'mdi-cog-outline',
    security: 'mdi-shield-alert',
    maintenance: 'mdi-wrench',
    operational: 'mdi-clipboard-alert'
  }
  return icons[type] || 'mdi-alert'
}

const getMessageClass = (message) => {
  const classes = ['message-card']
  if (!message.read) classes.push('message-unread')
  if (message.priority === 'high' || message.priority === 'critical') {
    classes.push('message-priority')
  }
  return classes.join(' ')
}

const getAlertClass = (alert) => {
  const classes = ['alert-card']
  classes.push(`alert-${alert.priority}`)
  return classes.join(' ')
}

const formatTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatDateTime = (timestamp) => {
  return new Date(timestamp).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const truncateText = (text, length) => {
  return text.length > length ? text.substring(0, length) + '...' : text
}

// Event handlers
const selectMessage = (message) => {
  console.log('💬 Seleccionar mensaje:', message.subject)
  // Implementar vista detallada del mensaje
}

const composeMessage = () => {
  console.log('✏️ Componer nuevo mensaje')
  // Implementar composer
}

const createAlert = () => {
  console.log('🚨 Crear nueva alerta')
  // Implementar creación de alerta
}

const acknowledgeAlert = (alert) => {
  console.log('✅ Reconocer alerta:', alert.title)
  emit('acknowledge-alert', alert.id)
}

const resolveAlert = (alert) => {
  console.log('✔️ Resolver alerta:', alert.title)
  emit('resolve-alert', alert.id)
}

const escalateAlert = (alert) => {
  console.log('⬆️ Escalar alerta:', alert.title)
  emit('escalate-alert', alert.id)
}

const shareAlert = (alert) => {
  console.log('📤 Compartir alerta:', alert.title)
  // Implementar compartir alerta
}

const sendMessage = (contact) => {
  console.log('💬 Enviar mensaje a:', contact.name)
  emit('send-message', contact.id)
}

const callContact = (contact) => {
  console.log('📞 Llamar a:', contact.name)
  // Implementar llamada
}

const locateContact = (contact) => {
  console.log('📍 Ubicar contacto:', contact.name)
  // Implementar localización
}
</script>

<style scoped>
.field-communications-panel {
  height: 100%;
}

.communications-content {
  height: 400px;
  overflow-y: auto;
}

.messages-list,
.alerts-list,
.contacts-list {
  max-height: 350px;
  overflow-y: auto;
}

.message-card {
  cursor: pointer;
  transition: all 0.2s ease;
}

.message-card:hover {
  background-color: rgba(0,0,0,0.02);
}

.message-unread {
  background-color: rgba(33, 150, 243, 0.05);
  border-left: 3px solid #2196f3;
}

.message-priority {
  border-left: 3px solid #ff9800;
}

.alert-card {
  transition: all 0.3s ease;
}

.alert-critical {
  background: rgba(244, 67, 54, 0.05);
  border-left: 4px solid #f44336;
}

.alert-high {
  background: rgba(255, 152, 0, 0.05);
  border-left: 4px solid #ff9800;
}

.alert-normal {
  border-left: 4px solid #2196f3;
}

.alert-low {
  border-left: 4px solid #4caf50;
}

.contact-actions {
  display: flex;
  gap: 4px;
}

.w-100 {
  width: 100%;
}
</style>
